import {injectIntl} from "react-intl";
import React, {useContext, useEffect, useRef, useState} from "react";
import {ZancaSortDeviceOverviewContext} from "./context/ZancaSortDeviceOverviewContext";
import Loader from "../../loader/Loader";
import Row from "@amzn/meridian/row"
import Column from "@amzn/meridian/column";
import ConfigurationTabHeader from "../../commonui/ConfigurationTabHeader";
import {DraftContext} from "../../draftSave/context/DraftSaveContext";
import {
    ASCENDING, ASSET_CONFIG_ATTRIBUTES, CONFIG_TYPE, OPERATION_COLOR, SEARCH_ROW_COLOR, ZANCASORT_ASSET_TYPE,
    ZANCASORT_LOCATION_TYPES, ZANCASORT_LOCATION_SUB_TYPES
} from "../../util/Constants";
import {parseLocationType} from "../../util/Validations";
import {getAssetByVersion, getAssetVersions} from "../../resources/AssetResource";
import RollbackModal from "../../rollback/RollbackModal";
import {chainWalk, getAssetRefreshInterval, getTestConfigKey, sortComparator} from "../../util/Util";
import PaginatedTable from "../../commonui/PaginatedTable";
import {TableCell, TableRow} from "@amzn/meridian/table";
import Text from "@amzn/meridian/text";
import Icon from "@amzn/meridian/icon";
import arrowRightTokens from "@amzn/meridian-tokens/base/icon/arrow-right-large";
import {getLocalizedMessage} from "../../util/Localization";
import Input from "@amzn/meridian/input";
import Button from "@amzn/meridian/button";
import plusCircleTokens from "@amzn/meridian-tokens/base/icon/plus-circle";
import AddZancaSortTestPackageModal from "./modal/AddZancaSortTestPackageModal";
import AddZancaSortLocationConfigurationModal from "./modal/AddZancaSortLocationConfigurationModal";
import DeleteZancaSortLocationConfigurationModal from "./modal/DeleteZancaSortLocationConfigurationModal";
import {GlobalStateContext} from "../../global/context/GlobalStateContext";
import {ZancaSortTestConfigCSVParser, ZancaSortLocationConfigCSVParser, ZancaSortTierConfigCSVParser} from "../../util/Parsers";
import UploadFileModal from "../../commonui/UploadFileModal";
import uploadTokens from "@amzn/meridian-tokens/base/icon/upload-small";
import Box from "@amzn/meridian/box";
import trashTokens from "@amzn/meridian-tokens/base/icon/trash";
import DeleteTestPackageConfigurationModal from "../../commonui/DeleteTestPackageConfigurationModal";
import AddZancaSortTierConfigModal from "./modal/AddZancaSortTierConfigModal";
import DeleteZancaSortTierConfigurationModal from "./modal/DeleteZancaSortTierConfigurationModal";
import {isNewZancaSortTestConfigEnabled} from '../../util/FeatureUtility';

const getLocationTypeDisplay = (locationType) => {
    const {type, subType} = parseLocationType(locationType);
    if (type === ZANCASORT_LOCATION_TYPES.TETRIS_STATION.value && subType && ZANCASORT_LOCATION_SUB_TYPES[subType]) {
        return ZANCASORT_LOCATION_TYPES.TETRIS_STATION.value + " (" + ZANCASORT_LOCATION_SUB_TYPES[subType].label + ")";
    }
    return locationType;
}

const ZS_CONFIG_ATTRIBUTES = ASSET_CONFIG_ATTRIBUTES[ZANCASORT_ASSET_TYPE];

const ZancaSortDeviceConfiguration = ({intl}) => {
    const isNewZancasortTestConfigEnabled = isNewZancaSortTestConfigEnabled();

    //Add Context
    const {viewModel, dispatch} = useContext(ZancaSortDeviceOverviewContext);
    const {getDraftData, setDraftData, resetDraftData, getFilteredDraftData, isDataEdited} = useContext(DraftContext);
    const { showCustomAlert } = useContext(GlobalStateContext);

    const [selectedMac, setSelectedMac] = useState("");
    const [valueConfiguration, setValueConfiguration] = useState("");
    const [rollBackVersions, setRollbackVersions] = useState([]);
    const [isVersionsLoading, setIsVersionsLoading] = useState(false);
    const [selectedVersion, setSelectedVersion] = useState("");

    // Modal state
    const [showRollbackModal, setShowRollbackModal] = useState(false)
    const [openAddModal, setOpenAddModal] = useState(false)
    const [openDeleteModal, setOpenDeleteModal] = useState(false)
    const [openCSVModal, setOpenCSVModal] = useState(false)
    const [openAddLocationModal, setOpenAddLocationModal] = useState(false)
    const [openDeleteLocationModal, setOpenDeleteLocationModal] = useState(false)
    const [openLocationCSVModal, setOpenLocationCSVModal] = useState(false)
    const [openAddTierModal, setOpenAddTierModal] = useState(false)
    const [openTierCSVModal, setOpenTierCSVModal] = useState(false)
    const [openDeleteTierModal, setOpenDeleteTierModal] = useState(false)

    // Search row state values
    const [scannablesSearchInput, setScannablesSearchInput] = useState('');
    const [destinationSearchInput, setDestinationSearchInput] = useState('');
    const [containerListSearchInput, setContainerListSearchInput] = useState('');
    const [locationIdSearchInput, setLocationIdSearchInput] = useState('');
    const [locationTypeSearchInput, setLocationTypeSearchInput] = useState('');

    //Data Sort prefs for test config table
    const [testConfigSortColumn, setTestConfigSortColumn] = useState("destination");
    const [testConfigSortOrder, setTestConfigSortOrder] = useState(ASCENDING);

    //Data Sort prefs for destination config table
    const [locationConfigSortColumn, setLocationConfigSortColumn] = useState("locationId");
    const [locationConfigSortOrder, setLocationConfigSortOrder] = useState(ASCENDING);

    //Data Sort prefs for tier config table
    const [tierConfigSortColumn, setTierConfigSortColumn] = useState("tierName");
    const [tierConfigSortOrder, setTierConfigSortOrder] = useState(ASCENDING);

    // Search row state values for tier config
    const [tierNameSearchInput, setTierNameSearchInput] = useState('');

    const tableEndRef = useRef(null);

    const PAGE_CONFIGURATION_KEYS = [CONFIG_TYPE.ZANCA_SORT_TEST_CONFIGURATION, CONFIG_TYPE.ZANCA_SORT_LOCATION_CONFIGURATION, CONFIG_TYPE.ZANCA_SORT_TIER_CONFIGURATION]

    useEffect(() => {
        if (!openAddModal && !openDeleteModal && !openAddLocationModal && !openDeleteLocationModal && !openLocationCSVModal) {
            getData()
            const interval = setInterval(() => {
                if (!document.hidden) {
                    getData();
                }
            }, getAssetRefreshInterval());
            return () => clearInterval(interval);
        }
    }, [openAddModal, openDeleteModal, openAddLocationModal, openDeleteLocationModal, openLocationCSVModal]);

    useEffect(() => {
        setDraftData({
            data: chainWalk(() => viewModel.testConfig[selectedMac], []),
            stateKey: CONFIG_TYPE.ZANCA_SORT_TEST_CONFIGURATION,
            subKey: selectedMac,
        })
        setDraftData({
            data: chainWalk(() => viewModel.locationConfig[selectedMac], []),
            stateKey: CONFIG_TYPE.ZANCA_SORT_LOCATION_CONFIGURATION,
            subKey: selectedMac,
        })
        setDraftData({
            data: chainWalk(() => viewModel.tierConfig[selectedMac], []),
            stateKey: CONFIG_TYPE.ZANCA_SORT_TIER_CONFIGURATION,
            subKey: selectedMac,
        })
    }, [selectedMac]);

    const getData = () => {
        if(isDataEdited(PAGE_CONFIGURATION_KEYS)) return;
        setOpenAddModal(false)
        setOpenDeleteModal(false)
        dispatch({type: 'LOAD_VIEW', data: {dispatcher: dispatch}});
    }

    const getModalContent = async () => {
        setIsVersionsLoading(true);
        let versionsData = await getAssetVersions(selectedMac);
        setRollbackVersions(versionsData.data.assetVersionDetailsList);
        setIsVersionsLoading(false);
    }

    const loadRollbackDataToDraft = async () => {
        let rollbackData = await getAssetByVersion(selectedMac, selectedVersion);
        const testConfigData = chainWalk(() => JSON.parse(rollbackData.data.asset.assetDetails.assetAttributes[ZS_CONFIG_ATTRIBUTES.TEST_CONFIG]), []);
        const locationConfigData = chainWalk(() => JSON.parse(rollbackData.data.asset.assetDetails.assetAttributes[ZS_CONFIG_ATTRIBUTES.LOCATION_CONFIG]), []);
        const tierConfigData = chainWalk(() => {
            const raw = JSON.parse(rollbackData.data.asset.assetDetails.assetAttributes[ZS_CONFIG_ATTRIBUTES.TIER_CONFIG]);
            return Object.entries(raw).map(([tierName, val]) => ({
                tierName,
                capacity: val.capacity,
                startLevel: parseInt(val.levels.split(':')[0], 10),
                endLevel: parseInt(val.levels.split(':')[1], 10)
            }));
        }, []);
        if(testConfigData.length) {
            setDraftData({
                data: testConfigData,
                stateKey: CONFIG_TYPE.ZANCA_SORT_TEST_CONFIGURATION,
                subKey: selectedMac,
                allRowsAdded: true,
                override: true
            });
        }
        if(locationConfigData.length) {
            setDraftData({
                data: locationConfigData,
                stateKey: CONFIG_TYPE.ZANCA_SORT_LOCATION_CONFIGURATION,
                subKey: selectedMac,
                allRowsAdded: true,
                override: true
            });
        }
        if(tierConfigData.length) {
            setDraftData({
                data: tierConfigData,
                stateKey: CONFIG_TYPE.ZANCA_SORT_TIER_CONFIGURATION,
                subKey: selectedMac,
                allRowsAdded: true,
                override: true
            });
        }
    }

    const updateTestConfigDraftData = (stowTestPackage) => {
        setDraftData({
            data: chainWalk(() => stowTestPackage, []),
            stateKey: CONFIG_TYPE.ZANCA_SORT_TEST_CONFIGURATION,
            subKey: selectedMac,
            allRowsAdded: true,
            override: true
        })
    }

    const updateLocationConfigDraftData = (locationConfig) => {
        setDraftData({
            data: chainWalk(() => locationConfig, []),
            stateKey: CONFIG_TYPE.ZANCA_SORT_LOCATION_CONFIGURATION,
            subKey: selectedMac,
            allRowsAdded: true,
            override: true
        })
    }

    const updateTierConfigDraftData = (tierConfig) => {
        setDraftData({
            data: chainWalk(() => tierConfig, []),
            stateKey: CONFIG_TYPE.ZANCA_SORT_TIER_CONFIGURATION,
            subKey: selectedMac,
            allRowsAdded: true,
            override: true
        })
    }

    const deployDraftZancaSortAsset = () => {
        const testPackageList = getFilteredDraftData({
            stateKey: CONFIG_TYPE.ZANCA_SORT_TEST_CONFIGURATION,
            subKey: selectedMac
        })

        const locationConfigList = getFilteredDraftData({
            stateKey: CONFIG_TYPE.ZANCA_SORT_LOCATION_CONFIGURATION,
            subKey: selectedMac
        })

        const tierConfigs = getFilteredDraftData({
            stateKey: CONFIG_TYPE.ZANCA_SORT_TIER_CONFIGURATION,
            subKey: selectedMac
        })

        dispatch({
            type: 'UPDATE_ZANCA_SORT_CONFIGURATION',
            data: {
                dispatcher: dispatch,
                showCustomAlert: showCustomAlert,
                configurations: {
                    testConfig: testPackageList,
                    locationConfig: locationConfigList,
                    tierConfig: tierConfigs
                },
                mac: selectedMac
            }
        });
        setDraftData({
            data: testPackageList,
            stateKey: CONFIG_TYPE.ZANCA_SORT_TEST_CONFIGURATION,
            subKey: selectedMac,
            override: true
        })
        setDraftData({
            data: locationConfigList,
            stateKey: CONFIG_TYPE.ZANCA_SORT_LOCATION_CONFIGURATION,
            subKey: selectedMac,
            override: true
        })
        setDraftData({
            data: tierConfigs,
            stateKey: CONFIG_TYPE.ZANCA_SORT_TIER_CONFIGURATION,
            subKey: selectedMac,
            override: true
        })
    }

    const scrollToBottom = () => {
        tableEndRef.current.scrollIntoView({behavior: "smooth"})
    }

    const sortTestConfigColumns = {
        scannables: (a, b, ascending) => {
            let scannableFirst = Array.isArray(a.scannables) ? a.scannables.join(' ') : a.scannables;
            let scannableSecond = Array.isArray(b.scannables) ? b.scannables.join(' ') : b.scannables;
            return ascending ? sortComparator(scannableFirst, scannableSecond) : -sortComparator(scannableFirst, scannableSecond);
        },
        destination: (a, b, ascending) => {
            return ascending ? sortComparator(a.destination, b.destination) : -sortComparator(a.destination, b.destination);
        },
        containerList: (a, b, ascending) => {
            let containerListFirst = (a && a.containerList) ?
                (Array.isArray(a.containerList) ? a.containerList.join(' ') : a.containerList) :
                '';
            let containerListSecond = (b && b.containerList) ?
                (Array.isArray(b.containerList) ? b.containerList.join(' ') : b.containerList) :
                '';
            return ascending ? sortComparator(containerListFirst, containerListSecond) :
                -sortComparator(containerListFirst, containerListSecond);
        }
    }

    const sortLocationConfigColumns = {
        locationId: (a, b, ascending) => {
            return ascending ? sortComparator(a.locationId, b.locationId) : -sortComparator(a.locationId, b.locationId);
        },
        locationType: (a, b, ascending) => {
            return ascending ? sortComparator(getLocationTypeDisplay(a.locationType), getLocationTypeDisplay(b.locationType))
                : -sortComparator(getLocationTypeDisplay(a.locationType), getLocationTypeDisplay(b.locationType));
        }
    }

    const sortTierConfigColumns = {
        tierName: (a, b, ascending) => {
            return ascending ? sortComparator(a.tierName, b.tierName) : -sortComparator(a.tierName, b.tierName);
        },
        capacity: (a, b, ascending) => {
            return ascending ? sortComparator(a.capacity, b.capacity) : -sortComparator(a.capacity, b.capacity);
        },
        startLevel: (a, b, ascending) => {
            return ascending ? sortComparator(a.startLevel, b.startLevel) : -sortComparator(a.startLevel, b.startLevel);
        },
        endLevel: (a, b, ascending) => {
            return ascending ? sortComparator(a.endLevel, b.endLevel) : -sortComparator(a.endLevel, b.endLevel);
        }
    }

    const searchDataForTestConfigTable = () => {
        let data = getDraftData({
            stateKey: CONFIG_TYPE.ZANCA_SORT_TEST_CONFIGURATION,
            subKey: selectedMac
        })

        data = data.filter(item => getTestConfigKey(item.scannables)
            .toLowerCase()
            .includes(scannablesSearchInput.toLowerCase()));

        if (isNewZancasortTestConfigEnabled) {
            data = data.filter(item => {
                    const destination = item && item.destination;
                    return destination ?
                        destination.toLowerCase().includes(destinationSearchInput.toLowerCase()) :
                        false;
                })
                .filter(item => {
                    const containerList = item && item.containerList;
                    const containerListString = containerList ?
                        (Array.isArray(containerList) ? containerList.join(' ') : containerList) :
                        '';
                    return containerListString.toLowerCase().includes(containerListSearchInput.toLowerCase());
                });
        } else {
            data = data.filter(item => item.destination
                .toLowerCase()
                .includes(destinationSearchInput.toLowerCase()));
        }
        data = data.sort((a, b) =>
            sortTestConfigColumns[testConfigSortColumn](a, b, testConfigSortOrder === ASCENDING)
        )
        return data;
    }

    const searchDataForLocationConfigTable = () => {
        let data = getDraftData({
            stateKey: CONFIG_TYPE.ZANCA_SORT_LOCATION_CONFIGURATION,
            subKey: selectedMac
        })

        data = data.filter(item => item.locationId
            .toLowerCase()
            .includes(locationIdSearchInput.toLowerCase()))
            .filter(item => getLocationTypeDisplay(item.locationType)
                .toLowerCase()
                .includes(locationTypeSearchInput.toLowerCase()));

        data = data.sort((a, b) =>
            sortLocationConfigColumns[locationConfigSortColumn](a, b, locationConfigSortOrder === ASCENDING)
        )
        return data;
    }

    const searchDataForTierConfigTable = () => {
        let data = getDraftData({
            stateKey: CONFIG_TYPE.ZANCA_SORT_TIER_CONFIGURATION,
            subKey: selectedMac
        })

        data = data.filter(item => item.tierName
            .toLowerCase()
            .includes(tierNameSearchInput.toLowerCase()));

        data = data.sort((a, b) =>
            sortTierConfigColumns[tierConfigSortColumn](a, b, tierConfigSortOrder === ASCENDING)
        )
        return data;
    }

    const getSortedDeviceList = () => {
        // Device List sorted based on device "name"
        const sortedDeviceList = Object.values(viewModel.zancaSortDeviceInformation);
        sortedDeviceList && sortedDeviceList.sort((a, b) => {
            if (a.name < b.name) {
                return -1
            } else if (b.name > a.name) {
                return 1
            }
            return 0
        })
        return sortedDeviceList
    }

    const getHeaderProps = () => {
        return {
            selectedMac,
            setSelectedMac,
            valueConfiguration,
            setValueConfiguration,
            defaultSelection: "",
            resetDraftData,
            deployDraft: deployDraftZancaSortAsset,
            scrollToBottom,
            sortedDeviceList: getSortedDeviceList(),
            configurationKeys: PAGE_CONFIGURATION_KEYS,
            refreshDeps: [viewModel.timestamp],
            isDeployDisabled: !isDataEdited(PAGE_CONFIGURATION_KEYS),
            isRollbackDisabled: !selectedMac || isDataEdited(PAGE_CONFIGURATION_KEYS),
            rollbackClickHandler: () => {getModalContent(); setShowRollbackModal(true)}
        }
    }

    function onClickAddSingle() {
        setOpenAddModal(true)
    }

    function onClickUploadCSV() {
        setOpenCSVModal(true)
    }

    function onClickDeleteConfiguration() {
        setOpenDeleteModal(true)
    }

    function onClickAddLocation() {
        setOpenAddLocationModal(true)
    }

    function onClickDeleteLocationConfiguration() {
        setOpenDeleteLocationModal(true)
    }

    function onClickUploadLocationCSV() {
        setOpenLocationCSVModal(true)
    }

    const renderTestConfigTableHeader = () => {
        return (
            <TableRow highlightOnHover={false}>
                <TableCell>
                    {getLocalizedMessage({intl, stringId: "table.index"})}
                </TableCell>
                <TableCell sortColumn="scannables">
                    {getLocalizedMessage({intl, stringId: "test.scannables"})}
                </TableCell>
                {isNewZancasortTestConfigEnabled ? (
                    [
                        <TableCell sortColumn="destination">
                            {getLocalizedMessage({intl, stringId: "test.sortZone"})}
                        </TableCell>,
                        <TableCell key="containerList" sortColumn="containerList">
                            {getLocalizedMessage({intl, stringId: "test.bag_list"})}
                        </TableCell>
                    ]
                ) : (
                    <TableCell sortColumn="destination">
                        {getLocalizedMessage({intl, stringId: "test.destination"})}
                    </TableCell>
                )}
            </TableRow>
        )
    }

    const renderSearchRow = () => {
        return (
            <TableRow backgroundColor={SEARCH_ROW_COLOR}>
                <TableCell/>
                <TableCell>
                    <Input
                        className="input-search-class"
                        value={scannablesSearchInput}
                        onChange={setScannablesSearchInput}
                        type="text"
                        placeholder="Search"
                        width="70%"
                        size="small"/>
                </TableCell>
                <TableCell>
                    <Input
                        className="input-search-class"
                        value={destinationSearchInput}
                        onChange={setDestinationSearchInput}
                        type="text"
                        placeholder="Search"
                        width="70%"
                        size="small"/>
                </TableCell>
                {isNewZancasortTestConfigEnabled ? (
                    <TableCell>
                        <Input
                            className="input-search-class"
                            value={containerListSearchInput}
                            onChange={setContainerListSearchInput}
                            type="text"
                            placeholder="Search"
                            width="70%"
                            size="small"/>
                    </TableCell>) : null
                }
            </TableRow>
        )
    }
    const renderTestConfigTableRow = (config, serialNumber) => {
        if (isNewZancasortTestConfigEnabled) {
            const sortZoneString = config.destination;
            const prevSortZoneString = chainWalk(() => config.prev.destination, null);
            const containerListString = config.containerList ?
                (Array.isArray(config.containerList) ? config.containerList.join(' ') : config.containerList) : '';
            const prevContainerListString = chainWalk(() =>
                    config.prev.containerList ?
                        (Array.isArray(config.prev.containerList) ? config.prev.containerList.join(' ') : config.prev.containerList)
                        : null,
                null
            );

            const sortZoneChanged = prevSortZoneString && sortZoneString !== prevSortZoneString;
            const containerListChanged = prevContainerListString && containerListString !== prevContainerListString;
            return (
                <TableRow highlightOnHover={true} backgroundColor={OPERATION_COLOR[config.operation]}>
                    <TableCell>
                        {serialNumber}
                    </TableCell>
                    <TableCell>
                        {Array.isArray(config.scannables) ? config.scannables.join(' ') : config.scannables}
                    </TableCell>
                    <TableCell>
                        {sortZoneChanged && <Text> {prevSortZoneString} </Text>}
                        {sortZoneChanged && <Icon tokens={arrowRightTokens}/>}
                        <Text> {sortZoneString} </Text>
                    </TableCell>
                    <TableCell>
                        {containerListChanged && <Text> {prevContainerListString} </Text>}
                        {containerListChanged && <Icon tokens={arrowRightTokens}/>}
                        <Text> {containerListString} </Text>
                    </TableCell>
                </TableRow>
            )
        } else {
            const dataString = config.destination;
            const prevDataString = chainWalk(() => config.prev.destination, null);
            return (
                <TableRow highlightOnHover={true} backgroundColor={OPERATION_COLOR[config.operation]}>
                    <TableCell>
                        {serialNumber}
                    </TableCell>
                    <TableCell>
                        {Array.isArray(config.scannables) ? config.scannables.join(' ') : config.scannables}
                    </TableCell>
                    <TableCell>
                        {prevDataString && <Text> {prevDataString} </Text>}
                        {prevDataString && <Icon tokens={arrowRightTokens}/>}
                        <Text> {dataString} </Text>
                    </TableCell>
                </TableRow>
            )
        }
    }

    const renderLocationConfigTableRow = (config) => {
        const locationIdString = config.locationId;
        const prevLocationIdString = chainWalk(() => config.prev.locationId, null);
        const locationTypeString = getLocationTypeDisplay(config.locationType);
        const prevLocationTypeString = chainWalk(() => getLocationTypeDisplay(config.prev.locationType), null);

        const locationIdChanged = prevLocationIdString && locationIdString !== prevLocationIdString;
        const locationTypeChanged = prevLocationTypeString && locationTypeString !== prevLocationTypeString;

        return (
            <TableRow highlightOnHover={true} backgroundColor={OPERATION_COLOR[config.operation]}>
                <TableCell>
                    {locationIdChanged && <Text> {prevLocationIdString} </Text>}
                    {locationIdChanged && <Icon tokens={arrowRightTokens}/>}
                    <Text> {locationIdString} </Text>
                </TableCell>
                <TableCell>
                    {locationTypeChanged && <Text> {prevLocationTypeString} </Text>}
                    {locationTypeChanged && <Icon tokens={arrowRightTokens}/>}
                    <Text> {locationTypeString} </Text>
                </TableCell>
            </TableRow>
        )
    }

    if (viewModel.loadingOverview) {
        return (<Loader/>)
    } else {
        return (
            <Column>
                <ConfigurationTabHeader {...getHeaderProps()}/>
                <RollbackModal
                    showRollbackModal={showRollbackModal}
                    setShowRollbackModal={setShowRollbackModal}
                    isVersionsLoading={isVersionsLoading}
                    selectedVersion={selectedVersion}
                    setSelectedVersion={setSelectedVersion}
                    rollBackVersions={rollBackVersions}
                    loadRollbackDataToDraft={loadRollbackDataToDraft}
                    intl={intl}
                />
                {valueConfiguration === CONFIG_TYPE.ZANCA_SORT_TEST_CONFIGURATION && selectedMac &&
                    <Column>
                        <PaginatedTable
                            data={searchDataForTestConfigTable()}
                            renderHeader={renderTestConfigTableHeader}
                            renderSearchRow={renderSearchRow}
                            renderRow={renderTestConfigTableRow}
                            sortColumn={testConfigSortColumn}
                            sortDirection={testConfigSortOrder}
                            onSort={({sortColumn, sortDirection}) => {
                                setTestConfigSortColumn(sortColumn);
                                setTestConfigSortOrder(sortDirection);
                            }}
                        />
                        <Row width="100%" widths={["100", "100", "fill", "100"]}>
                            <Button onClick={onClickAddSingle}
                                    type="primary"
                                    size="small">
                                <Icon tokens={plusCircleTokens}/>
                                {getLocalizedMessage({intl, stringId: "stowtestconfig.addupdate"})}
                            </Button>
                            <Button onClick={onClickUploadCSV}
                                    type="primary"
                                    size="small">
                                <Icon tokens={uploadTokens}/>
                                {getLocalizedMessage({intl, stringId: "stowtestconfig.uploadCSV"})}
                            </Button>
                            <Box></Box>
                            <Button onClick={onClickDeleteConfiguration}
                                    size="small" type="tertiary">
                                <Icon tokens={trashTokens}/>
                                {getLocalizedMessage({intl, stringId: "stowtestconfig.deleteconfig"})}
                            </Button>
                        </Row>
                        <AddZancaSortTestPackageModal mac={selectedMac}
                                                      open={openAddModal} setOpen={setOpenAddModal}/>
                        <UploadFileModal mac={selectedMac}
                                         open={openCSVModal} setOpen={setOpenCSVModal}
                                         parser={ZancaSortTestConfigCSVParser} updateConfig={updateTestConfigDraftData}/>
                        <DeleteTestPackageConfigurationModal mac={selectedMac}
                                                             testConfig={CONFIG_TYPE.ZANCA_SORT_TEST_CONFIGURATION}
                                                             open={openDeleteModal} setOpen={setOpenDeleteModal}/>
                        <div ref={tableEndRef}/>
                    </Column>
                }
                {valueConfiguration === CONFIG_TYPE.ZANCA_SORT_LOCATION_CONFIGURATION && selectedMac &&
                    <Column>
                        <PaginatedTable
                            data={searchDataForLocationConfigTable()}
                            renderHeader={() => (
                                <TableRow highlightOnHover={false}>
                                    <TableCell sortColumn="locationId">
                                        {getLocalizedMessage({intl, stringId: "location.id"})}
                                    </TableCell>
                                    <TableCell sortColumn="locationType">
                                        {getLocalizedMessage({intl, stringId: "location.type"})}
                                    </TableCell>
                                </TableRow>
                            )}
                            renderSearchRow={() => (
                                <TableRow backgroundColor={SEARCH_ROW_COLOR}>
                                    <TableCell>
                                        <Input
                                            className="input-search-class"
                                            value={locationIdSearchInput}
                                            onChange={setLocationIdSearchInput}
                                            type="text"
                                            placeholder="Search"
                                            width="70%"
                                            size="small"/>
                                    </TableCell>
                                    <TableCell>
                                        <Input
                                            className="input-search-class"
                                            value={locationTypeSearchInput}
                                            onChange={setLocationTypeSearchInput}
                                            type="text"
                                            placeholder="Search"
                                            width="70%"
                                            size="small"/>
                                    </TableCell>
                                </TableRow>
                            )}
                            renderRow={renderLocationConfigTableRow}
                            sortColumn={locationConfigSortColumn}
                            sortDirection={locationConfigSortOrder}
                            onSort={({sortColumn, sortDirection}) => {
                                setLocationConfigSortColumn(sortColumn);
                                setLocationConfigSortOrder(sortDirection);
                            }}
                        />
                        <Row width="100%" widths={["100", "100", "fill", "100"]}>
                            <Button
                                    onClick={onClickAddLocation}
                                    type="primary"
                                    size="small">
                                <Icon tokens={plusCircleTokens}/>
                                {getLocalizedMessage({intl, stringId: "locationconfig.addupdate"})}
                            </Button>
                            <Button
                                    onClick={onClickUploadLocationCSV}
                                    type="primary"
                                    size="small">
                                <Icon tokens={uploadTokens}/>
                                {getLocalizedMessage({intl, stringId: "locationconfig.uploadCSV"})}
                            </Button>
                            <Box></Box>
                            <Button
                                    onClick={onClickDeleteLocationConfiguration}
                                    size="small"
                                    type="tertiary">
                                <Icon tokens={trashTokens}/>
                                {getLocalizedMessage({intl, stringId: "locationconfig.deleteconfig"})}
                            </Button>
                        </Row>
                        <AddZancaSortLocationConfigurationModal mac={selectedMac}
                                                                   open={openAddLocationModal}
                                                                   setOpen={setOpenAddLocationModal}/>
                        <UploadFileModal mac={selectedMac}
                                         open={openLocationCSVModal} setOpen={setOpenLocationCSVModal}
                                         parser={ZancaSortLocationConfigCSVParser} updateConfig={updateLocationConfigDraftData}/>
                        <DeleteZancaSortLocationConfigurationModal mac={selectedMac}
                                                                      open={openDeleteLocationModal}
                                                                      setOpen={setOpenDeleteLocationModal}/>
                        <div ref={tableEndRef}/>
                    </Column>
                }
                {valueConfiguration === CONFIG_TYPE.ZANCA_SORT_TIER_CONFIGURATION && selectedMac &&
                    <Column>
                        <PaginatedTable
                            data={searchDataForTierConfigTable()}
                            renderHeader={() => (
                                <TableRow highlightOnHover={false}>
                                    <TableCell sortColumn="tierName">
                                        {getLocalizedMessage({intl, stringId: "tierconfig.tierName"})}
                                    </TableCell>
                                    <TableCell sortColumn="capacity">
                                        {getLocalizedMessage({intl, stringId: "tierconfig.capacity"})}
                                    </TableCell>
                                    <TableCell sortColumn="startLevel">
                                        {getLocalizedMessage({intl, stringId: "tierconfig.startLevel"})}
                                    </TableCell>
                                    <TableCell sortColumn="endLevel">
                                        {getLocalizedMessage({intl, stringId: "tierconfig.endLevel"})}
                                    </TableCell>
                                </TableRow>
                            )}
                            renderSearchRow={() => (
                                <TableRow backgroundColor={SEARCH_ROW_COLOR}>
                                    <TableCell>
                                        <Input
                                            className="input-search-class"
                                            value={tierNameSearchInput}
                                            onChange={setTierNameSearchInput}
                                            type="text"
                                            placeholder="Search"
                                            width="70%"
                                            size="small"/>
                                    </TableCell>
                                    <TableCell/>
                                    <TableCell/>
                                    <TableCell/>
                                </TableRow>
                            )}
                            renderRow={(config) => {
                                const tierNameString = config.tierName;
                                const prevTierNameString = chainWalk(() => config.prev.tierName, null);
                                const capacityChanged = chainWalk(() => config.prev.capacity, null) !== null
                                    && config.capacity !== config.prev.capacity;
                                const startLevelChanged = chainWalk(() => config.prev.startLevel, null) !== null
                                    && config.startLevel !== config.prev.startLevel;
                                const endLevelChanged = chainWalk(() => config.prev.endLevel, null) !== null
                                    && config.endLevel !== config.prev.endLevel;
                                const tierNameChanged = prevTierNameString && tierNameString !== prevTierNameString;

                                return (
                                    <TableRow highlightOnHover={true} backgroundColor={OPERATION_COLOR[config.operation]}>
                                        <TableCell>
                                            {tierNameChanged && <Text> {prevTierNameString} </Text>}
                                            {tierNameChanged && <Icon tokens={arrowRightTokens}/>}
                                            <Text> {tierNameString} </Text>
                                        </TableCell>
                                        <TableCell>
                                            {capacityChanged && <Text> {config.prev.capacity} </Text>}
                                            {capacityChanged && <Icon tokens={arrowRightTokens}/>}
                                            <Text> {config.capacity} </Text>
                                        </TableCell>
                                        <TableCell>
                                            {startLevelChanged && <Text> {config.prev.startLevel} </Text>}
                                            {startLevelChanged && <Icon tokens={arrowRightTokens}/>}
                                            <Text> {config.startLevel} </Text>
                                        </TableCell>
                                        <TableCell>
                                            {endLevelChanged && <Text> {config.prev.endLevel} </Text>}
                                            {endLevelChanged && <Icon tokens={arrowRightTokens}/>}
                                            <Text> {config.endLevel} </Text>
                                        </TableCell>
                                    </TableRow>
                                )
                            }}
                            sortColumn={tierConfigSortColumn}
                            sortDirection={tierConfigSortOrder}
                            onSort={({sortColumn, sortDirection}) => {
                                setTierConfigSortColumn(sortColumn);
                                setTierConfigSortOrder(sortDirection);
                            }}
                        />
                        <Row width="100%" widths={["100", "100", "fill", "100"]}>
                            <Button
                                    onClick={() => setOpenAddTierModal(true)}
                                    type="primary"
                                    size="small">
                                <Icon tokens={plusCircleTokens}/>
                                {getLocalizedMessage({intl, stringId: "tierconfig.addupdate"})}
                            </Button>
                            <Button
                                    onClick={() => setOpenTierCSVModal(true)}
                                    type="primary"
                                    size="small">
                                <Icon tokens={uploadTokens}/>
                                {getLocalizedMessage({intl, stringId: "tierconfig.uploadCSV"})}
                            </Button>
                            <Box></Box>
                            <Button
                                    onClick={() => setOpenDeleteTierModal(true)}
                                    size="small"
                                    type="tertiary">
                                <Icon tokens={trashTokens}/>
                                {getLocalizedMessage({intl, stringId: "tierconfig.deleteconfig"})}
                            </Button>
                        </Row>
                        <AddZancaSortTierConfigModal mac={selectedMac}
                                                     open={openAddTierModal}
                                                     setOpen={setOpenAddTierModal}/>
                        <UploadFileModal mac={selectedMac}
                                         open={openTierCSVModal} setOpen={setOpenTierCSVModal}
                                         parser={ZancaSortTierConfigCSVParser} updateConfig={updateTierConfigDraftData}/>
                        <DeleteZancaSortTierConfigurationModal mac={selectedMac}
                                                               open={openDeleteTierModal}
                                                               setOpen={setOpenDeleteTierModal}/>
                        <div ref={tableEndRef}/>
                    </Column>
                }
            </Column>
        )
    }
}

export default injectIntl(ZancaSortDeviceConfiguration)


// WEBPACK FOOTER //
// ./src/components/devicemanagement/zancasort/ZancaSortDeviceConfiguration.js